import { NOTIFICATION_TYPES, NotificationsUtils } from "@/lib/notifications";
import { useAuthStore } from "@/store";
import { useNotificationStore as useNotificationStoreAuth } from "@/store/notificationStore";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom hook for managing notifications in the application
 * @returns {Object} Notification utilities and state
 */
export function useNotifications() {
  const { user } = useAuthStore();
  const userId = user?.id;
  const initRef = useRef(false);
  const pollingIntervalRef = useRef(null);

  // Get notification state from store
  const {
    notifications,
    unreadCount,
    initialized,
    setInitialized,
    fetchNotifications,
    markAllAsRead,
    markAsRead,
    deleteNotification,
    activeTab,
    setActiveTab,
    getFilteredNotifications,
    createNotification: storeCreateNotification,
    error,
    isLoading,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    setIsLoading,
  } = useNotificationStoreAuth();

  // Local state
  const [pollingInterval, setPollingInterval] = useState(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  // Filter notifications based on active tab
  const filteredNotifications = notifications.filter((notification) => {
    if (activeTab === "all") return true;
    return notification.type === activeTab;
  });

  // Initialize notifications when user is available
  useEffect(() => {
    const initNotifications = async () => {
      if (!user || initialized || initRef.current) return;

      try {
        // Set loading and initialization flags
        setIsLoading(true);
        initRef.current = true;

        // Fetch notifications from the server
        await fetchNotifications(user.id);

        // Set up real-time subscription
        subscribeToNotifications(user.id);

        // Mark as initialized
        setInitialized(true);
      } catch (error) {
        console.error("Failed to initialize notifications:", error);
      } finally {
        setIsLoading(false);
        initRef.current = false;
      }
    };

    initNotifications();

    // Set up polling for new notifications every minute
    if (user && initialized && !pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        fetchNotifications(user.id);
      }, 60000); // Poll every minute
    }

    // Cleanup function
    return () => {
      // Clear polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Unsubscribe from real-time updates when component unmounts
      if (user && initialized) {
        unsubscribeFromNotifications();
      }
    };
  }, [
    user,
    initialized,
    fetchNotifications,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    setInitialized,
  ]);

  /**
   * Create a new notification
   * @param {Object} notificationData - Notification data
   * @returns {Promise<Object>} - Result of the creation operation
   */
  const sendNotification = useCallback(
    async (notificationData) => {
      if (!userId) {
        return { success: false, error: "User ID is required" };
      }

      try {
        // Create the notification using the utility function
        const result = await NotificationsUtils.createNotification({
          userId,
          ...notificationData,
        });

        return result;
      } catch (error) {
        console.error("Error sending notification:", error);
        return { success: false, error: error.message };
      }
    },
    [userId]
  );

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = useCallback(() => {
    if (userId) {
      markAllAsRead(userId);
    }
  }, [userId, markAllAsRead]);

  /**
   * Create a system notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {Object} data - Additional data
   */
  const sendSystemNotification = useCallback(
    (title, message, data = {}) => {
      return sendNotification({
        type: NOTIFICATION_TYPES.SYSTEM,
        title,
        message,
        data,
      });
    },
    [sendNotification]
  );

  /**
   * Create a message notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {Object} data - Additional data
   */
  const sendMessageNotification = useCallback(
    (title, message, data = {}) => {
      return sendNotification({
        type: NOTIFICATION_TYPES.MESSAGE,
        title,
        message,
        data,
      });
    },
    [sendNotification]
  );

  /**
   * Create a workspace notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {Object} data - Additional data
   */
  const sendWorkspaceNotification = useCallback(
    (title, message, data = {}) => {
      return sendNotification({
        type: NOTIFICATION_TYPES.WORKSPACE,
        title,
        message,
        data,
      });
    },
    [sendNotification]
  );

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle opening notifications
  const handleOpenNotifications = () => {
    setIsNotificationsOpen(true);
  };

  // Handle closing notifications
  const handleCloseNotifications = () => {
    setIsNotificationsOpen(false);
  };

  // Toggle notifications open/closed
  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  // Handle mark single notification as read
  const handleMarkAsRead = async (notificationId) => {
    if (!userId) return;
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error(
        `Failed to mark notification ${notificationId} as read:`,
        error
      );
    }
  };

  // Handle deleting a notification
  const handleDeleteNotification = async (notificationId) => {
    if (!userId) return;
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error(`Failed to delete notification ${notificationId}:`, error);
    }
  };

  return {
    // State
    notifications: filteredNotifications,
    unreadCount,
    initialized,
    activeTab,
    error,
    isLoading,
    isNotificationsOpen,

    // Getters
    getFilteredNotifications,

    // Actions
    setActiveTab: handleTabChange,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification,
    sendNotification,
    sendSystemNotification,
    sendMessageNotification,
    sendWorkspaceNotification,
    toggleNotifications,
    handleOpenNotifications,
    handleCloseNotifications,

    // Constants
    NOTIFICATION_TYPES,
  };
}

export default useNotifications;
